/**
 * Ayuno con el método de comidas (Req. 23; design.md, 8.10).
 *
 * El cálculo no se guarda: sale de las comidas cada vez. Lo que se guarda es
 * lo que la persona confirma o corrige, en `fasts`, conservando el cálculo
 * original y los valores anteriores de cada corrección (Req. 23.11).
 *
 * El temporizador, el registro a mano y el historial con la racha llegan en
 * la entrega siguiente; esto no los adelanta.
 */

import { actualizarSettings, db, leerSettings } from './schema';
import { enEscritura } from './tx';
import { nuevoId } from './ids';
import { historialMetasManualesDe, leerMetasDelDia } from './metas';
import { metasDelDia, metasManualesVigentes } from '@/domain/etapas';
import type { Fast, MealEntry, MetodoAyuno } from './types';
import { ahoraIso, esFechaLocal, sumarDias, type FechaLocal } from '@/domain/dates';
import { ErrorValidacion } from '@/domain/errors';
import { esHoraLocal, fechaLocalDe, instanteLocal, type HoraLocal } from '@/domain/time';
import {
  avisoDeAyunoLargo,
  ayunoEnCurso,
  ayunoRegistradoDe,
  calcularAyunoQueTermina,
  estadoDelDia,
  eventosDeAyuno,
  resumenDeAyunos,
  ventanaDeAlimentacion,
  type AvisoDeAyunoLargo,
  type AyunoEnCurso,
  type DiaDeAyuno,
  type EstadoAyuno,
  type Periodo,
  type ResumenDeAyunos,
  type VentanaDeAlimentacion,
} from '@/domain/ayuno';

export interface AyunoDelDia {
  /** El ayuno que termina ese día, con su estado. */
  estado: EstadoAyuno;
  /** El que va corriendo ahora; null si no hay registros recientes o si se está comiendo. */
  enCurso: AyunoEnCurso | null;
  /** La ventana de alimentación abierta; null si se está ayunando (Req. 23.14). */
  ventana: VentanaDeAlimentacion | null;
  /** La meta de la etapa vigente o, sin etapa, la de Ajustes (Req. 23.7). */
  metaHoras: number | null;
  /** Con comidas o con temporizador (Req. 23.1). */
  metodo: MetodoAyuno;
  /** El ayuno abierto del temporizador, si hay. */
  abierto: Fast | null;
  /** Aviso de más de 24 h que todavía no se ha cerrado (Req. 23.9). */
  avisoLargo: AvisoDeAyunoLargo | null;
}

/** Las comidas del día y del anterior, que es todo lo que el cálculo necesita. */
async function datosDe(dia: FechaLocal): Promise<{ entradas: MealEntry[]; incompleto: (f: FechaLocal) => boolean }> {
  const ayer = sumarDias(dia, -1);
  const [entradas, deAyer, deHoy] = await Promise.all([
    db.mealEntries.where('fecha').between(ayer, dia, true, true).toArray(),
    db.days.get(ayer),
    db.days.get(dia),
  ]);
  const incompletos = new Set(
    [
      [ayer, deAyer?.registroIncompleto ?? false],
      [dia, deHoy?.registroIncompleto ?? false],
    ]
      .filter(([, v]) => v === true)
      .map(([f]) => f as FechaLocal),
  );
  return { entradas, incompleto: (f) => incompletos.has(f) };
}

/** LECTURA PURA, apta para useLiveQuery: el ayuno de un día y el que va corriendo. */
export async function leerAyunoDelDia(dia: FechaLocal, ahora: Date = new Date()): Promise<AyunoDelDia> {
  const [{ entradas, incompleto }, ayunos, metas, ajustes] = await Promise.all([
    datosDe(dia),
    db.fasts.toArray(),
    leerMetasDelDia(dia),
    leerSettings(),
  ]);
  const eventos = eventosDeAyuno(entradas);
  const metaHoras = metas.metas.ayunoHoras;
  const cerradaEn = ajustes.ventanaCerradaEn ?? null;
  const estado = estadoDelDia(dia, eventos, ayunos, { incompleto }, metaHoras);
  const enCurso = ayunoEnCurso(ahora, eventos, ayunos, metaHoras, cerradaEn);
  return {
    estado,
    enCurso,
    ventana: ventanaDeAlimentacion(ahora, eventos, ayunos, cerradaEn),
    metaHoras,
    metodo: ajustes.metodoAyuno,
    abierto: ayunos.find((a) => a.fin === null) ?? null,
    avisoLargo: avisoDeAyunoLargo(dia, enCurso, estado, ajustes.avisoAyunoLargoVisto ?? null),
  };
}

/** El cálculo de ese día, tal como está ahora. Lanza si no se puede calcular. */
async function periodoCalculado(dia: FechaLocal): Promise<Periodo> {
  const { entradas, incompleto } = await datosDe(dia);
  const calculo = calcularAyunoQueTermina(dia, eventosDeAyuno(entradas), { incompleto });
  if (calculo.tipo !== 'calculado') {
    throw new ErrorValidacion('Ese ayuno ya no se puede calcular con tus comidas: revisa los registros de esos días.');
  }
  return calculo.periodo;
}

async function metaDe(dia: FechaLocal): Promise<number | null> {
  return (await leerMetasDelDia(dia)).metas.ayunoHoras;
}

/**
 * Confirma el ayuno calculado de un día (Req. 23.2): se guarda con origen
 * `comidas` y con el cálculo en `calculado`, para avisar si las comidas
 * cambian después (Req. 23.13). Dos toques no duplican. ESCRIBE.
 */
export async function confirmarAyuno(dia: FechaLocal, id: string = nuevoId('ayu')): Promise<Fast> {
  if (!esFechaLocal(dia)) throw new ErrorValidacion('La fecha no es válida.');
  const periodo = await periodoCalculado(dia);
  const metaHoras = await metaDe(dia);
  return enEscritura([db.fasts], async () => {
    const ayunos = await db.fasts.toArray();
    const existente = ayunoRegistradoDe(ayunos, dia);
    if (existente !== null) throw new ErrorValidacion('Ese día ya tiene un ayuno registrado.');
    const ahora = ahoraIso();
    const ayuno: Fast = {
      id,
      inicio: periodo.inicio,
      fin: periodo.fin,
      metaHoras,
      origen: 'comidas',
      calculado: periodo,
      historial: [],
      nota: null,
      avisoLargoMostrado: false,
      creadoEn: ahora,
      actualizadoEn: ahora,
    };
    await db.fasts.put(ayuno);
    return ayuno;
  });
}

export interface HorasDeAyuno {
  /** El día en que empezó, que puede ser el anterior. */
  fechaInicio: FechaLocal;
  horaInicio: HoraLocal;
  fechaFin: FechaLocal;
  horaFin: HoraLocal;
}

/**
 * Corrige las horas del ayuno de un día (Req. 23.11). Se guarda con origen
 * `correccion`, conserva el cálculo original y mete los valores anteriores en
 * `historial`. Si ese día no tenía ayuno guardado, se crea con las horas
 * indicadas. ESCRIBE.
 */
export async function corregirAyuno(dia: FechaLocal, horas: HorasDeAyuno, id: string = nuevoId('ayu')): Promise<Fast> {
  if (!esFechaLocal(dia)) throw new ErrorValidacion('La fecha no es válida.');
  for (const f of [horas.fechaInicio, horas.fechaFin]) {
    if (!esFechaLocal(f)) throw new ErrorValidacion('La fecha no es válida.', 'fecha');
  }
  for (const h of [horas.horaInicio, horas.horaFin]) {
    if (!esHoraLocal(h)) throw new ErrorValidacion('La hora no es válida.', 'hora');
  }
  const inicio = instanteLocal(horas.fechaInicio, horas.horaInicio);
  const fin = instanteLocal(horas.fechaFin, horas.horaFin);
  if (Date.parse(fin) <= Date.parse(inicio)) {
    throw new ErrorValidacion('El fin del ayuno debe ser posterior a su inicio.', 'hora');
  }
  const metaHoras = await metaDe(dia);
  // Lo que la app calculaba, para no perderlo. Se lee antes de abrir la
  // transacción, que solo alcanza a `fasts` (Req. 1.4)
  const calculado = await calculoSiSePuede(dia);
  return enEscritura([db.fasts], async () => {
    const ayunos = await db.fasts.toArray();
    const existente = ayunoRegistradoDe(ayunos, dia);
    const ahora = ahoraIso();
    if (existente === null) {
      const ayuno: Fast = {
        id,
        inicio,
        fin,
        metaHoras,
        origen: 'correccion',
        calculado,
        historial: [],
        nota: null,
        avisoLargoMostrado: false,
        creadoEn: ahora,
        actualizadoEn: ahora,
      };
      await db.fasts.put(ayuno);
      return ayuno;
    }
    const corregido: Fast = {
      ...existente,
      inicio,
      fin,
      origen: 'correccion',
      historial: [...existente.historial, { cambiadoEn: ahora, inicio: existente.inicio, fin: existente.fin }],
      actualizadoEn: ahora,
    };
    await db.fasts.put(corregido);
    return corregido;
  });
}

/** El cálculo de ese día si se puede calcular; null si no. */
async function calculoSiSePuede(dia: FechaLocal): Promise<Periodo | null> {
  try {
    return await periodoCalculado(dia);
  } catch {
    return null;
  }
}

/**
 * Cuando las comidas cambian después de confirmar (Req. 23.13): usar el
 * cálculo nuevo, o conservar las horas confirmadas. Conservarlas las guarda
 * como una decisión (origen `correccion`), así no se vuelve a preguntar; el
 * cálculo original se conserva en los dos casos. ESCRIBE.
 */
export async function resolverCambioDeAyuno(dia: FechaLocal, cual: 'nuevo' | 'confirmado'): Promise<Fast> {
  if (!esFechaLocal(dia)) throw new ErrorValidacion('La fecha no es válida.');
  const periodo = cual === 'nuevo' ? await periodoCalculado(dia) : null;
  return enEscritura([db.fasts], async () => {
    const ayunos = await db.fasts.toArray();
    const existente = ayunoRegistradoDe(ayunos, dia);
    if (existente === null) throw new ErrorValidacion('Ese día ya no tiene un ayuno registrado.');
    const ahora = ahoraIso();
    const actualizado: Fast =
      periodo !== null
        ? {
            ...existente,
            inicio: periodo.inicio,
            fin: periodo.fin,
            calculado: periodo,
            historial: [...existente.historial, { cambiadoEn: ahora, inicio: existente.inicio, fin: existente.fin }],
            actualizadoEn: ahora,
          }
        : { ...existente, origen: 'correccion', actualizadoEn: ahora };
    await db.fasts.put(actualizado);
    return actualizado;
  });
}

// ---------------------------------------------------------------------------
// Historial (Req. 23.8, 23.12; 0.6.1)
// ---------------------------------------------------------------------------

export interface AyunosRecientes {
  /** Un día por renglón, del más reciente al más antiguo. */
  dias: DiaDeAyuno[];
  resumen: ResumenDeAyunos;
}

/**
 * Los últimos días con su ayuno y el resumen (Req. 23.8). La meta de cada día
 * es la de su etapa o, sin etapa, la de Ajustes de ese día: un cambio de
 * metas no reescribe los días anteriores. LECTURA PURA.
 */
export async function leerAyunosRecientes(hasta: FechaLocal, cuantos = 30): Promise<AyunosRecientes> {
  if (!esFechaLocal(hasta)) throw new ErrorValidacion('La fecha no es válida.');
  const desde = sumarDias(hasta, -(cuantos - 1));
  // Un día antes del rango: el primer ayuno empieza el día anterior
  const [entradas, marcados, ayunos, etapas, mantenimientos, settings] = await Promise.all([
    db.mealEntries.where('fecha').between(sumarDias(desde, -1), hasta, true, true).toArray(),
    db.days.where('fecha').between(sumarDias(desde, -1), hasta, true, true).toArray(),
    db.fasts.toArray(),
    db.stages.toArray(),
    db.maintenance.toArray(),
    leerSettings(),
  ]);
  const eventos = eventosDeAyuno(entradas);
  const incompletos = new Set(marcados.filter((d) => d.registroIncompleto).map((d) => d.fecha));
  const dias: DiaDeAyuno[] = [];
  const historial = historialMetasManualesDe(settings);
  for (let i = 0; i < cuantos; i++) {
    const fecha = sumarDias(hasta, -i);
    const metaHoras = metasDelDia(fecha, etapas, mantenimientos, metasManualesVigentes(historial, fecha)).ayunoHoras;
    dias.push({
      fecha,
      metaHoras,
      estado: estadoDelDia(fecha, eventos, ayunos, { incompleto: (f) => incompletos.has(f) }, metaHoras),
    });
  }
  return { dias, resumen: resumenDeAyunos(dias, hasta) };
}

// ---------------------------------------------------------------------------
// Temporizador y aviso de ayuno largo (Req. 23.1, 23.4, 23.6, 23.9; 0.6.2)
// ---------------------------------------------------------------------------

/** El ayuno abierto del temporizador, si hay. LECTURA PURA. */
export async function leerAyunoAbierto(): Promise<Fast | null> {
  const abiertos = (await db.fasts.toArray()).filter((a) => a.fin === null);
  return abiertos[abiertos.length - 1] ?? null;
}

/**
 * Empieza un ayuno con el temporizador (Req. 23.4). Solo uno a la vez; su
 * meta es la del día, para saber a qué hora se cumple. ESCRIBE.
 */
export async function iniciarAyuno(ahora: Date = new Date(), id: string = nuevoId('ayu')): Promise<Fast> {
  const hoy = fechaLocalDe(ahora.toISOString());
  const metaHoras = await metaDe(hoy);
  return enEscritura([db.fasts], async () => {
    const abiertos = (await db.fasts.toArray()).filter((a) => a.fin === null);
    if (abiertos.length > 0) throw new ErrorValidacion('Ya tienes un ayuno en curso.');
    const inicio = ahora.toISOString();
    // `creadoEn` es cuándo se guardó, no cuándo empezó el ayuno: al cerrar la
    // ventana de alimentación el inicio queda en el pasado (la última
    // ingesta) y `ayunoRegistradoDe` ordena por `creadoEn` para quedarse con
    // lo último que hizo la persona
    const guardadoEn = ahoraIso();
    const ayuno: Fast = {
      id,
      inicio,
      fin: null,
      metaHoras,
      origen: 'temporizador',
      calculado: null,
      historial: [],
      nota: null,
      avisoLargoMostrado: false,
      creadoEn: guardadoEn,
      actualizadoEn: guardadoEn,
    };
    await db.fasts.put(ayuno);
    return ayuno;
  });
}

/**
 * "Empecé antes" (Req. 23.4): corrige la hora de inicio del ayuno en curso,
 * las veces que haga falta, guardando las anteriores en `historial`. No puede
 * quedar en el futuro ni a más de dos días. ESCRIBE.
 */
export async function corregirInicioEnCurso(inicioDe: { fecha: FechaLocal; hora: HoraLocal }, ahora: Date = new Date()): Promise<Fast> {
  if (!esFechaLocal(inicioDe.fecha)) throw new ErrorValidacion('La fecha no es válida.', 'fecha');
  if (!esHoraLocal(inicioDe.hora)) throw new ErrorValidacion('La hora no es válida.', 'hora');
  const inicio = instanteLocal(inicioDe.fecha, inicioDe.hora);
  if (Date.parse(inicio) > ahora.getTime()) throw new ErrorValidacion('Ese inicio todavía no llega.', 'hora');
  if (ahora.getTime() - Date.parse(inicio) > 2 * 24 * 3_600_000) {
    throw new ErrorValidacion('Un ayuno en curso no puede llevar más de dos días: revisa la fecha.', 'fecha');
  }
  return enEscritura([db.fasts], async () => {
    const abiertos = (await db.fasts.toArray()).filter((a) => a.fin === null);
    const abierto = abiertos[abiertos.length - 1];
    if (abierto === undefined) throw new ErrorValidacion('No tienes un ayuno en curso.');
    const momento = ahoraIso();
    const corregido: Fast = {
      ...abierto,
      inicio,
      historial: [...abierto.historial, { cambiadoEn: momento, inicio: abierto.inicio, fin: abierto.fin }],
      actualizadoEn: momento,
    };
    await db.fasts.put(corregido);
    return corregido;
  });
}

/**
 * Termina el ayuno en curso (Req. 23.4). Si queda por debajo de la meta se
 * guarda igual, como incompleto: no se borra (Req. 23.6). ESCRIBE.
 */
export async function terminarAyuno(ahora: Date = new Date()): Promise<Fast> {
  return enEscritura([db.fasts], async () => {
    const abiertos = (await db.fasts.toArray()).filter((a) => a.fin === null);
    const abierto = abiertos[abiertos.length - 1];
    if (abierto === undefined) throw new ErrorValidacion('No tienes un ayuno en curso.');
    const fin = ahora.toISOString();
    if (Date.parse(fin) <= Date.parse(abierto.inicio)) {
      throw new ErrorValidacion('El fin del ayuno debe ser posterior a su inicio.');
    }
    const terminado: Fast = { ...abierto, fin, actualizadoEn: fin };
    await db.fasts.put(terminado);
    return terminado;
  });
}

// ---------------------------------------------------------------------------
// Ventana de alimentación (Req. 23.14; 0.16.0)
// ---------------------------------------------------------------------------

/**
 * Cierra el ayuno abierto cuando se registra un alimento que lo rompe, con
 * la HORA DE CONSUMO de ese alimento, no la de captura. ESCRIBE.
 *
 * La llama `guardarComida` dentro de su misma transacción, así que registrar
 * el desayuno ya no deja un ayuno corriendo que haya que terminar a mano.
 *
 * No lanza nunca, a propósito: un ayuno que no se puede cerrar (por ejemplo,
 * porque la comida quedó antes del inicio del ayuno) no debe impedir guardar
 * lo que se comió. Y no toca nada más: los ayunos ya cerrados, confirmados o
 * corregidos se quedan como están.
 */
export async function cerrarAyunoAbiertoAlComer(momento: string): Promise<Fast | null> {
  const fin = Date.parse(momento);
  if (Number.isNaN(fin)) return null;
  const abiertos = (await db.fasts.toArray()).filter((a) => a.fin === null);
  const abierto = abiertos[abiertos.length - 1];
  if (abierto === undefined) return null;
  // Comer antes de que empezara el ayuno no lo cierra: no tendría sentido
  if (fin <= Date.parse(abierto.inicio)) return null;
  const terminado: Fast = { ...abierto, fin: momento, actualizadoEn: ahoraIso() };
  await db.fasts.put(terminado);
  return terminado;
}

/**
 * Cierra la ventana de alimentación (Req. 23.14): desde aquí empieza el ayuno
 * siguiente, contado desde la ÚLTIMA INGESTA, no desde el momento del toque.
 *
 * Con el método de temporizador también arranca el ayuno en `fasts`, con esa
 * misma hora, que es lo que espera quien lleva el ayuno así. Con el método de
 * comidas no se guarda ningún ayuno: ese sigue saliendo de los registros y se
 * confirma como siempre, para no duplicarlo.
 *
 * Volver a comer después vuelve a abrir la ventana sola. ESCRIBE.
 */
export async function cerrarVentanaDeAlimentacion(ahora: Date = new Date()): Promise<string> {
  const hoy = fechaLocalDe(ahora.toISOString());
  const { entradas } = await datosDe(hoy);
  const ajustes = await leerSettings();
  const eventos = eventosDeAyuno(entradas);
  const ayunos = await db.fasts.toArray();
  const ventana = ventanaDeAlimentacion(ahora, eventos, ayunos, ajustes.ventanaCerradaEn ?? null);
  if (ventana === null) {
    throw new ErrorValidacion('No tienes una ventana de alimentación abierta.');
  }
  if (ajustes.metodoAyuno === 'temporizador' && !ayunos.some((a) => a.fin === null)) {
    await iniciarAyuno(new Date(ventana.ultimaIngesta));
  }
  await actualizarSettings({ ventanaCerradaEn: ventana.ultimaIngesta });
  return ventana.ultimaIngesta;
}

/** El método con que se lleva el ayuno (Req. 23.1). ESCRIBE. */
export async function elegirMetodoDeAyuno(metodo: MetodoAyuno): Promise<void> {
  if (metodo !== 'comidas' && metodo !== 'temporizador') throw new ErrorValidacion('Elige comidas o temporizador.');
  await actualizarSettings({ metodoAyuno: metodo });
}

/** Cierra el aviso de un ayuno de más de 24 h, para no repetirlo (Req. 23.9). ESCRIBE. */
export async function marcarAvisoLargoVisto(clave: string): Promise<void> {
  await actualizarSettings({ avisoAyunoLargoVisto: clave });
}
